import { formatInTimeZone } from "date-fns-tz";
import { BadgeColor } from "@pushpress/shared-components";
import {
  AppointmentScheduleToCalendarFragment,
  CalendarItemFragment,
} from "./Calendar-generated.hooks";
import { joinStrings } from "../../utils/misc";
import { ICalendarFilter } from "./Calendar.types";

export function differenceInMinutes(
  timestamp1: number,
  timestamp2: number,
): number {
  // Calculate the difference in milliseconds
  const differenceInMilliseconds = timestamp2 - timestamp1;

  // Convert the difference to minutes
  return differenceInMilliseconds / (1000 * 60);
}

const MINUTE = 1;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatRegistrationStart(
  registrationStartOffset?: number,
): string {
  if (!registrationStartOffset || registrationStartOffset === 0) {
    return "Immediately";
  }

  if (registrationStartOffset > -HOUR && registrationStartOffset < 0) {
    return `${Math.abs(registrationStartOffset)} min before start time`;
  }

  if (registrationStartOffset > -DAY && registrationStartOffset < 0) {
    const hours = Math.floor(Math.abs(registrationStartOffset) / HOUR);
    return `${hours} hour${hours > 1 ? "s" : ""} before start time`;
  }

  if (registrationStartOffset > -7 * DAY && registrationStartOffset < 0) {
    const days = Math.floor(Math.abs(registrationStartOffset) / DAY);
    return `${days} day${days > 1 ? "s" : ""} before start time`;
  }

  const weeks = Math.floor(Math.abs(registrationStartOffset) / (7 * DAY));
  return `${weeks} week${weeks > 1 ? "s" : ""} before start time`;
}

export function formatRegistrationEnd(registrationEndOffset?: number): string {
  if (!registrationEndOffset || registrationEndOffset === 0) {
    return "At class start time";
  }

  if (registrationEndOffset > -HOUR && registrationEndOffset < 0) {
    return `${Math.abs(registrationEndOffset)} min before start time`;
  }

  if (registrationEndOffset > -DAY && registrationEndOffset < 0) {
    const hours = Math.floor(Math.abs(registrationEndOffset) / HOUR);
    return `${hours} hour${hours > 1 ? "s" : ""} before start time`;
  }

  if (registrationEndOffset > -7 * DAY && registrationEndOffset < 0) {
    const days = Math.floor(Math.abs(registrationEndOffset) / DAY);
    return `${days} day${days > 1 ? "s" : ""} before start time`;
  }

  const weeks = Math.floor(Math.abs(registrationEndOffset) / (7 * DAY));
  return `${weeks} week${weeks > 1 ? "s" : ""} before start time`;
}

const attendeeStatuses = ["invited", "reserved", "checkedin", "no show"];

// coaches is an optional param that expects array of userUuid's to filter out of attendees list
export function filterAttendees<
  T extends { uuid: string; status: string; userUuid: string },
>(attendees: T[], coaches: string[] = []): T[] {
  return attendees.filter((attendee) => {
    const isCoach =
      coaches.length &&
      attendee.userUuid &&
      coaches.includes(attendee.userUuid);
    return (
      attendeeStatuses.includes(attendee.status) &&
      !isCoach && // filter out attendees who are coaches
      attendee.uuid
    );
  });
}

export function filterWaitlist<T extends { uuid: string; status: string }>(
  attendees: T[],
): T[] {
  return attendees.filter(
    (attendee) => attendee.status === "waitlisted" && attendee.uuid,
  );
}

export function filterLateCancel<T extends { uuid: string; status: string }>(
  attendees: T[],
): T[] {
  return attendees.filter(
    (attendee) => attendee.status === "late cancel" && attendee.uuid,
  );
}

export const chipColor = {
  invited: BadgeColor.Gray,
  reserved: BadgeColor.Info,
  checkedin: BadgeColor.Success,
  "no show": BadgeColor.Error,
  cancelled: BadgeColor.Error,
  "late cancel": BadgeColor.Error,
  waitlisted: BadgeColor.Warning,
};

export const statusLabelMap = {
  invited: "INVITED",
  reserved: "RESERVED",
  checkedin: "CHECKED IN",
  "no show": "NO SHOW",
  cancelled: "CANCELED",
  "late cancel": "LATE CANCEL",
  waitlisted: "WAIT LISTED",
};

export const waitlistExpireHours: {
  label: string;
  value: string | number;
}[] = [
  {
    label: "0 hours",
    value: 0,
  },
  {
    label: "1 hour",
    value: 1,
  },
  {
    label: "2 hours",
    value: 2,
  },
  {
    label: "3 hours",
    value: 3,
  },
  {
    label: "4 hours",
    value: 4,
  },
  {
    label: "5 hours",
    value: 5,
  },
  {
    label: "6 hours",
    value: 6,
  },
  {
    label: "8 hours",
    value: 8,
  },
  {
    label: "9 hours",
    value: 9,
  },
  {
    label: "12 hours",
    value: 12,
  },
  {
    label: "18 hours",
    value: 18,
  },
  {
    label: "24 hours",
    value: 24,
  },
];

export const waitlistExpireMinutes: {
  label: string;
  value: string | number;
}[] = [
  {
    label: "0 minutes",
    value: 0,
  },
  {
    label: "15 minutes",
    value: 15,
  },
  {
    label: "30 minutes",
    value: 30,
  },
  {
    label: "45 minutes",
    value: 45,
  },
];

export function getWaitlistExpirationOffset(hour: number, minutes: number) {
  return hour * 60 + minutes;
}

export const calendarItemDoesNotRepeatOption: {
  label: string;
  value: number;
} = {
  label: "Does not repeat",
  value: 0,
};

export const calendarItemRecurrence: {
  label: string;
  value: number;
}[] = [
  {
    label: "Every week",
    value: 1,
  },
  {
    label: "Every 2 weeks",
    value: 2,
  },
  {
    label: "Every 3 weeks",
    value: 3,
  },
  {
    label: "Every 4 weeks",
    value: 4,
  },
  calendarItemDoesNotRepeatOption,
];

export const reservationsOpenOptions: {
  label: string;
  value: number;
}[] = [
  { label: "Immediately", value: 0 },
  { label: "15 minutes before start time", value: -15 },
  { label: "30 minutes before start time", value: -30 },
  { label: "45 minutes before start time", value: -45 },
  { label: "1 hour before start time", value: -60 },
  { label: "2 hours before start time", value: -120 },
  { label: "3 hours before start time", value: -180 },
  { label: "4 hours before start time", value: -240 },
  { label: "6 hours before start time", value: -360 },
  { label: "8 hours before start time", value: -480 },
  { label: "10 hours before start time", value: -600 },
  { label: "12 hours before start time", value: -720 },
  { label: "1 day before start time", value: -1440 },
  { label: "2 days before start time", value: -2880 },
  { label: "3 days before start time", value: -4320 },
  { label: "4 days before start time", value: -5760 },
  { label: "5 days before start time", value: -7200 },
  { label: "6 days before start time", value: -8640 },
  { label: "1 week before start time", value: -10080 },
  { label: "2 weeks before start time", value: -20160 },
  { label: "3 weeks before start time", value: -30240 },
  { label: "4 weeks before start time", value: -40320 },
];

export const reservationsCloseOptions: {
  label: string;
  value: number;
  disabled?: boolean;
}[] = [
  { label: "at start time", value: 0 },
  { label: "15 minutes before start time", value: -15 },
  { label: "30 minutes before start time", value: -30 },
  { label: "45 minutes before start time", value: -45 },
  { label: "1 hour before start time", value: -60 },
  { label: "2 hours before start time", value: -120 },
  { label: "3 hours before start time", value: -180 },
  { label: "4 hours before start time", value: -240 },
  { label: "6 hours before start time", value: -360 },
  { label: "8 hours before start time", value: -480 },
  { label: "10 hours before start time", value: -600 },
  { label: "12 hours before start time", value: -720 },
  { label: "1 day before start time", value: -1440 },
  { label: "2 days before start time", value: -2880 },
  { label: "3 days before start time", value: -4320 },
  { label: "4 days before start time", value: -5760 },
  { label: "5 days before start time", value: -7200 },
  { label: "6 days before start time", value: -8640 },
  { label: "1 week before start time", value: -10080 },
  { label: "2 weeks before start time", value: -20160 },
  { label: "3 weeks before start time", value: -30240 },
  { label: "4 weeks before start time", value: -40320 },
  { label: "---------------------------------", value: null, disabled: true },
  { label: "30 minutes after class start time", value: 30 },
  { label: "1 hour after class start time", value: 60 },
  { label: "2 hours after class start time", value: 120 },
  { label: "3 hours after class start time", value: 180 },
  { label: "4 hours after class start time", value: 240 },
  { label: "6 hours after class start time", value: 360 },
  { label: "8 hours after class start time", value: 480 },
  { label: "12 hours after class start time", value: 720 },
  { label: "16 hours after class start time", value: 960 },
];

export const lateCancelOptions: {
  label: string;
  value: number;
}[] = [
  { label: "at start time", value: 0 },
  { label: "15 minutes before start time", value: -15 },
  { label: "30 minutes before start time", value: -30 },
  { label: "45 minutes before start time", value: -45 },
  { label: "1 hour before start time", value: -60 },
  { label: "2 hours before start time", value: -120 },
  { label: "3 hours before start time", value: -180 },
  { label: "4 hours before start time", value: -240 },
  { label: "6 hours before start time", value: -360 },
  { label: "8 hours before start time", value: -480 },
  { label: "10 hours before start time", value: -600 },
  { label: "12 hours before start time", value: -720 },
  { label: "1 day before start time", value: -1440 },
  { label: "2 days before start time", value: -2880 },
  { label: "3 days before start time", value: -4320 },
  { label: "4 days before start time", value: -5760 },
  { label: "5 days before start time", value: -7200 },
  { label: "6 days before start time", value: -8640 },
  { label: "1 week before start time", value: -10080 },
  { label: "2 weeks before start time", value: -20160 },
  { label: "3 weeks before start time", value: -30240 },
  { label: "4 weeks before start time", value: -40320 },
];

export function filterCalendarItems(
  items: CalendarItemFragment[],
  filter?: ICalendarFilter,
): CalendarItemFragment[] {
  // filter events with duration equal to zero. There is a validation to prevent that but it is good to avoid some old data
  const validItems = items.filter((item) => {
    return item.endDatetime > item.startDatetime;
  });

  const hasSomeCheckbox =
    filter?.classes || filter?.appointments || filter?.events;
  return validItems.filter((item) => {
    // not show if it is class and it is not checked
    if (hasSomeCheckbox && item.type === "Class" && !filter?.classes) {
      return false;
    }
    // not show if it is event and it is not checked
    if (hasSomeCheckbox && item.type === "Event" && !filter?.events) {
      return false;
    }
    // not show if it doesn't have the location
    if (
      filter?.locations?.length &&
      !filter?.locations.some((loc) => loc.uuid === item.location?.uuid)
    ) {
      return false;
    }
    // not show if it doesn't have the coach
    if (filter?.staff?.length) {
      const hasCoach = filter.staff.some(
        (s) => s.user_uuid === item.mainCoach?.userUuid,
      );
      const hasAssistantCoach = filter.staff.some(
        (s) => s.user_uuid === item.assistantCoach?.userUuid,
      );
      if (!hasCoach && !hasAssistantCoach) {
        return false;
      }
    }
    return true;
  });
}

export function filterAppointmentsItems(
  items: AppointmentScheduleToCalendarFragment[],
  filter?: ICalendarFilter,
): AppointmentScheduleToCalendarFragment[] {
  // not show if it is appointment and it is not checked
  const hasSomeCheckbox =
    filter?.classes || filter?.appointments || filter?.events;
  if (hasSomeCheckbox && !filter?.appointments) {
    return [];
  }
  return items.filter((item) => {
    // not show canceled appointments
    if (item.eventStatus === "CANCELLED") {
      return false;
    }
    // not show if it doesn't have the staff
    if (
      filter?.staff?.length &&
      !filter?.staff.some((s) => s.user_uuid === item.staff?.uuid)
    ) {
      return false;
    }
    // not show if the filter has locations
    if (filter?.locations?.length) {
      return false;
    }
    return true;
  });
}

export function getCalendarFilterTagsMap(filter?: ICalendarFilter) {
  const result = {};

  // classes tag
  if (filter?.classes) {
    result["classes"] = {
      label: "Classes",
      onDelete: (f: ICalendarFilter) => ({
        ...f,
        classes: undefined,
      }),
    };
  }

  // classes tag
  if (filter?.appointments) {
    result["appointments"] = {
      label: "Appointments",
      onDelete: (f: ICalendarFilter) => ({
        ...f,
        appointments: undefined,
      }),
    };
  }

  // events tag
  if (filter?.events) {
    result["events"] = {
      label: "Events",
      onDelete: (f: ICalendarFilter) => ({
        ...f,
        events: undefined,
      }),
    };
  }

  // tags by location
  const locations = filter?.locations || [];
  locations.forEach((s) => {
    const key = `loc-${s.uuid}`;
    const label = s.name;
    result[key] = {
      label,
      onDelete: (f: ICalendarFilter) => ({
        ...f,
        locations: f.locations.filter((item) => item.uuid !== s.uuid),
      }),
    };
  });

  // tags by staff
  const staff = filter?.staff || [];
  staff.forEach((s) => {
    const key = `staff-${s.user_uuid}`;
    const label = joinStrings(s.first_name, s.last_name);
    result[key] = {
      label,
      onDelete: () => ({
        ...filter,
        staff: filter?.staff.filter((item) => item.user_uuid !== s.user_uuid),
      }),
    };
  });

  return result;
}

export enum CalendarItemSessionTypes {
  All = 0,
  Classes = 2,
  Events = 1,
}

const ISO_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";

export const convertToTimezone = (date: Date, timezone: string) => {
  const formatted = formatInTimeZone(date, timezone, ISO_DATE_FORMAT);
  return new Date(formatted);
};
